import m from '@bitstillery/mithril'
import {MithrilComponent} from '@bitstillery/mithril'
import {state} from '@bitstillery/mithril'
import {$t, api, notifier} from '@bitstillery/common/app'
import {Button, DataCard, Icon} from '@bitstillery/common/components'
import {classes, merge_deep, route_param_text} from '@bitstillery/common/lib/utils'
import {$s} from '@bitstillery/common/app'
import {CampaignMnemonic} from '@bitstillery/common/api/fact2server'
import {format_iso_to_relative_with_time} from '@bitstillery/common/lib/format'

import {Survey} from './survey'

import type {DiscoGetUserSubscriptionResponse, GetUserSubscriptionResponse} from '@bitstillery/common/api/fact2server'

interface CampaignPreferencesAttrs {
    contact_person_artkey?: number
    disabled?: boolean
    /** When set (e.g. portal My portal), row actions use `type="info"` like other portal forms. */
    portal_action_buttons?: boolean
    relation_artkey?: number
}

interface CampaignPreferencesData {
    admin_mode: boolean
    /** Discover's admin view gets the unsubscribe details (`<campaign>_on`, `_by_name`, …) as well. */
    form: (GetUserSubscriptionResponse & Partial<DiscoGetUserSubscriptionResponse>) | null
    loading: boolean
    price_list_uuid: string | undefined
    selected_campaign: CampaignMnemonic | null
    unsubscribe_survey: boolean
}

export class CampaignPreferences extends MithrilComponent<CampaignPreferencesAttrs> {
    data: CampaignPreferencesData = state(
        {
            admin_mode: process.env.MSI_PACKAGE === 'discover',
            // Null until the subscriptions load.
            form: null,
            loading: true,
            price_list_uuid: route_param_text(m.route.param('price_list_uuid')),
            selected_campaign: null,
            unsubscribe_survey: false,
        },
        'campaign-preferences-data',
    )

    override async oninit(vnode: m.Vnode<CampaignPreferencesAttrs>) {
        await this.fetch_campaign_subscriptions(vnode)
    }

    /** Discover passes the contact person it edits; the portal user manages their own subscriptions. */
    subscriptions_endpoint(attrs: CampaignPreferencesAttrs): string {
        const {contact_person_artkey, relation_artkey} = attrs
        if (contact_person_artkey !== undefined && relation_artkey !== undefined) {
            return `discover/relations/${relation_artkey}/contact-persons/${contact_person_artkey}/subscriptions`
        }
        return 'portal/user/subscriptions'
    }

    async fetch_campaign_subscriptions(vnode: m.Vnode<CampaignPreferencesAttrs>) {
        const endpoint = this.subscriptions_endpoint(vnode.attrs)
        if ($s.identity.token) {
            const {result, success} = await api.get(endpoint)
            if (success) {
                merge_deep(this.data, {form: result, loading: false})
            }
        } else {
            const {result, success} = await api.get(endpoint, {price_list_uuid: this.data.price_list_uuid})
            if (success) {
                merge_deep(this.data, {form: result, loading: false})
            } else {
                notifier.notify($t('campaign_preferences.email.invalid_link'), 'warning')
                void m.route.set('/', {}, {replace: true})
            }
        }
    }

    async update_campaign_subscription(
        vnode: m.Vnode<CampaignPreferencesAttrs>,
        campaign: CampaignMnemonic,
        is_unsubscribed: boolean,
        reason: string = '',
        comment: string,
    ) {
        const data: {comment?: string; is_unsubscribed: boolean; reason?: string} = {is_unsubscribed}
        if (reason) {
            data.reason = reason
        }
        if (comment) {
            data.comment = comment
        }

        let endpoint = `${this.subscriptions_endpoint(vnode.attrs)}/${campaign}`
        if (process.env.MSI_PACKAGE !== 'discover' && this.data.price_list_uuid) {
            endpoint += `?price_list_uuid=${this.data.price_list_uuid}`
        }

        const {success} = await api.post(endpoint, data, true)
        if (success) {
            notifier.notify($t('campaign_preferences.email.saved_confirmation'), 'info')
        } else {
            notifier.notify($t('campaign_preferences.email.invalid_link'), 'warning')
            void m.route.set('/', {}, {replace: true})
        }
    }

    view(vnode: m.Vnode<CampaignPreferencesAttrs>) {
        const {form, selected_campaign} = this.data
        return (
            <div className='c-campaign-preferences'>
                <div className='campaigns'>
                    {form &&
                        Object.values(CampaignMnemonic).map((campaign_name: CampaignMnemonic) => {
                            const is_unsubscribed = form[campaign_name]
                            const reason = form[`${campaign_name}_reason`]

                            return (
                                <div
                                    className={classes('campaign', {
                                        disabled: vnode.attrs.disabled,
                                        subscribed: !is_unsubscribed,
                                    })}
                                >
                                    <div className='campaign-info'>
                                        <div className='header'>
                                            <Icon name={campaign_name} size='l' type='info' />
                                            <div className='title'>{$t(`profile.campaigns.${campaign_name}`)}</div>
                                        </div>
                                        {!this.data.admin_mode || !is_unsubscribed ? (
                                            <div className='description'>{$t(`profile.campaigns.${campaign_name}_help`)}</div>
                                        ) : (
                                            <div className='description'>
                                                <DataCard
                                                    model={{
                                                        data: [
                                                            {
                                                                label: 'Unsubscribed on',
                                                                value: format_iso_to_relative_with_time(
                                                                    form[`${campaign_name}_on`],
                                                                ),
                                                            },
                                                            {
                                                                label: 'Disabled by',
                                                                value: form[`${campaign_name}_by_name`]
                                                                    ? form[`${campaign_name}_by_name`]
                                                                    : 'Contact person',
                                                            },
                                                            {
                                                                label: 'Reason',
                                                                value: reason
                                                                    ? $t(`campaign_preferences.survey.reasons.${reason}`)
                                                                    : null,
                                                            },
                                                            {
                                                                label: 'Comment',
                                                                value: form[`${campaign_name}_comment`],
                                                            },
                                                        ],
                                                    }}
                                                    variant='dense'
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className='actions'>
                                        <Button
                                            disabled={this.data.loading}
                                            icon='email'
                                            onclick={async () => {
                                                this.data.selected_campaign = campaign_name
                                                if (is_unsubscribed) {
                                                    await this.update_campaign_subscription(vnode, campaign_name, false, '', '')
                                                    await this.fetch_campaign_subscriptions(vnode)
                                                } else {
                                                    this.data.unsubscribe_survey = true
                                                }
                                            }}
                                            size='s'
                                            text={
                                                is_unsubscribed
                                                    ? $t('campaign_preferences.buttons.signup')
                                                    : $t('campaign_preferences.buttons.unsubscribe')
                                            }
                                            tip={() => {
                                                if (is_unsubscribed) {
                                                    return $t('campaign_preferences.actions.subscribe_tip')
                                                }
                                                return $t('campaign_preferences.actions.unsubscribe_tip')
                                            }}
                                            type={
                                                vnode.attrs.portal_action_buttons
                                                    ? 'info'
                                                    : is_unsubscribed
                                                      ? 'success'
                                                      : 'default'
                                            }
                                            variant={vnode.attrs.portal_action_buttons ? 'cta' : 'action'}
                                        />
                                    </div>
                                </div>
                            )
                        })}
                </div>

                {selected_campaign && this.data.unsubscribe_survey && (
                    <Survey
                        campaign={selected_campaign}
                        onsubmit={async (is_unsubscribed: boolean, reason: string, comment: string) => {
                            await this.update_campaign_subscription(vnode, selected_campaign, is_unsubscribed, reason, comment)
                            await this.fetch_campaign_subscriptions(vnode)
                        }}
                        onclose={() => {
                            this.data.unsubscribe_survey = false
                        }}
                    />
                )}
            </div>
        )
    }
}
